import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, ChevronRight, Loader2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { ActivityType, ItineraryActivity } from "../models/activity.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";
import { useActivityLibraryQuery } from "@/lib/library/hooks/useActivityLibrary";
import { LibraryActivity } from "@/lib/library/models/library-activity.model";

interface LibraryPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Tipo di attività su cui filtrare la libreria (accommodation, activity, flight, ...). */
  activityType: ActivityType;
  /** Data del giorno corrente nel builder, usata per valorizzare l'attività risultante. */
  date?: string;
  onSelect: (activity: ItineraryActivity) => void;
}

/**
 * Modal per scegliere un'attività dalla libreria personale/di agenzia e aggiungerla
 * al giorno corrente dell'itinerario, in alternativa alla creazione manuale.
 */
export function LibraryPickerModal({
  isOpen,
  onClose,
  activityType,
  date,
  onSelect,
}: LibraryPickerModalProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Reset filtri ogni volta che il modal si chiude, per non mostrare uno stato
  // "sporco" la prossima volta che viene riaperto per un tipo diverso.
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedTag(null);
    }
  }, [isOpen]);

  // La query è filtrata server-side per tipo attività e parte solo a modal aperto:
  // ogni tipo ha la sua query key (e la sua cache), così scorrere il builder non
  // scarica mai l'intera libreria. Ricerca e tag restano filtri client-side sul
  // sottoinsieme del tipo corrente.
  const { data: activitiesForType = [], isLoading } = useActivityLibraryQuery(
    { activityType },
    { enabled: isOpen }
  );

  // Replica client-side lo stesso match testuale della RPC get_activity_library
  // (ILIKE case-insensitive su title/description/address).
  // Nota: `location` NON è un indirizzo/luogo in questo modello — è l'indice d'ordine
  // dell'attività nel giorno (vedi itinerary-builder.model.ts `addActivity`), quindi
  // il campo mostrato/cercato come "luogo" è `address`, non `location`.
  const searchedActivities = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (!searchLower) return activitiesForType;
    return activitiesForType.filter(
      (activity) =>
        activity.title.toLowerCase().includes(searchLower) ||
        (activity.description ?? '').toLowerCase().includes(searchLower) ||
        (activity.address ?? '').toLowerCase().includes(searchLower)
    );
  }, [activitiesForType, search]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    searchedActivities.forEach((activity) => {
      activity.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [searchedActivities]);

  const filteredActivities = useMemo(() => {
    if (!selectedTag) return searchedActivities;
    return searchedActivities.filter((activity) => activity.tags.includes(selectedTag));
  }, [searchedActivities, selectedTag]);

  const handleSelect = (activity: LibraryActivity) => {
    onSelect(activity.toItineraryActivity(date));
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {getActivityTypeIcon(activityType)} {t('library.pickerTitle')}
        </div>
      }
      size="md"
    >
      <div className="p-4 flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('library.pickerSearchPlaceholder')}
            className="pl-9"
          />
        </div>

        {availableTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedTag(null)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                !selectedTag
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('library.pickerAllTags')}
            </button>
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSelectedTag(tag)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  selectedTag === tag
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
            </div>
          ) : filteredActivities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {t('library.pickerEmptyState')}
            </p>
          ) : (
            filteredActivities.map((activity) => (
              <button
                key={activity.id}
                type="button"
                onClick={() => handleSelect(activity)}
                className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 text-left w-full transition-colors hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900 truncate">{activity.title}</p>
                    {activity.agency_id !== null && (
                      <Badge variant="secondary">{t('library.pickerAgencyBadge')}</Badge>
                    )}
                  </div>
                  {activity.address && (
                    <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{activity.address}</span>
                    </p>
                  )}
                  {activity.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {activity.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
